import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faMinusCircle } from "@fortawesome/pro-solid-svg-icons/faMinusCircle";
import { faPlusCircle } from "@fortawesome/pro-solid-svg-icons/faPlusCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { defineMessages, useIntl } from "react-intl";

import { Group, MapEntity } from "@mapmycustomers/shared/types/entity";

import RoundButton from "@app/component/RoundButton";
import ButtonType from "@app/enum/ButtonType";
import {
  bulkAddPinsToGroup,
  bulkRemovePinsFromGroup,
} from "@app/scene/map/store/groupMode/actions";
import { hideAnnotation, setPinHover } from "@app/scene/map/store/mapMode/actions";
import { mapEntityIdGetter } from "@app/util/map/idGetters";

interface Props {
  addPinsToGroup: typeof bulkAddPinsToGroup.request;
  entity?: MapEntity;
  group?: Group;
  onHideAnnotation: typeof hideAnnotation;
  removePinsFromGroup: typeof bulkRemovePinsFromGroup.request;
  setPinHover: typeof setPinHover;
}

const messages = defineMessages({
  addToGroup: {
    id: "map.pin.annotation.actionsBar.addToGroup",
    defaultMessage: "Add to Group",
    description: "Entity actions bar - add to group",
  },
  removeFromGroup: {
    id: "map.pin.annotation.actionsBar.removeFromGroup",
    defaultMessage: "Remove from Group",
    description: "Entity actions bar - remove from group",
  },
});

const GroupModeEntityActions: React.FC<Props> = ({
  addPinsToGroup,
  entity,
  group,
  onHideAnnotation,
  removePinsFromGroup,
  setPinHover,
}) => {
  const intl = useIntl();

  const groupId = group?.id;
  const isVisible = groupId && entity;
  const canRemove = !!entity?.groups?.find((group) => group.id === groupId);
  const canAdd = !canRemove;

  const handleAdd = useCallback(() => {
    if (entity) {
      addPinsToGroup({ entities: [entity] });
      onHideAnnotation();
      setPinHover(mapEntityIdGetter(entity));
    }
  }, [addPinsToGroup, entity, onHideAnnotation, setPinHover]);

  const handleRemove = useCallback(() => {
    if (entity) {
      removePinsFromGroup({ entities: [entity] });
      onHideAnnotation();
      setPinHover(mapEntityIdGetter(entity));
    }
  }, [entity, removePinsFromGroup, onHideAnnotation, setPinHover]);

  return isVisible ? (
    <>
      {canAdd && (
        <RoundButton leftIcon={<FontAwesomeIcon icon={faPlusCircle} />} onClick={handleAdd}>
          {intl.formatMessage(messages.addToGroup)}
        </RoundButton>
      )}

      {canRemove && (
        <RoundButton
          leftIcon={<FontAwesomeIcon icon={faMinusCircle} />}
          onClick={handleRemove}
          type={ButtonType.DANGER}
        >
          {intl.formatMessage(messages.removeFromGroup)}
        </RoundButton>
      )}
    </>
  ) : null;
};

const mapDispatchToProps = {
  addPinsToGroup: bulkAddPinsToGroup.request,
  onHideAnnotation: hideAnnotation,
  removePinsFromGroup: bulkRemovePinsFromGroup.request,
  setPinHover,
};

export default connect(null, mapDispatchToProps)(GroupModeEntityActions);
